import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  text: string;
  miniModel: string;
  year: number;
  avatar: string;
}

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Sarah Johnson',
      location: 'Johannesburg, SA',
      rating: 5,
      text: 'The team transformed my 1975 Mini Cooper from a rust bucket into a showroom beauty. The autobody work and attention to detail is incredible, and the passion they have for these classic cars really shows in their work.',
      miniModel: '1975 Mini Cooper',
      year: 2023,
      avatar: 'SJ'
    },
    {
      id: 2,
      name: 'David Thompson',
      location: 'Cape Town, SA',
      rating: 5,
  text: 'I\'ve been restoring classics for 20 years, and Gerrie\'s Classic Minis is by far the best autobody shop I\'ve worked with. They sourced original parts I thought were impossible to find and completed the restoration ahead of schedule.',
      miniModel: '1969 Mini Cooper S',
      year: 2023,
      avatar: 'DT'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      location: 'Durban, SA',
      rating: 5,
      text: 'The bodywork and paint on my Mini was outstanding. The staff explained everything clearly and provided regular updates. My 1980 Mini looks better now than it did when it was new!',
      miniModel: '1980 Mini Clubman',
      year: 2024,
      avatar: 'ER'
    },
    {
      id: 4,
      name: 'James Wilson',
      location: 'Pretoria, SA',
      rating: 5,
      text: 'Professional, honest, and passionate about their craft. They didn\'t just fix my Mini - they brought it back to life. The custom paintwork and bodywork is absolutely stunning.',
      miniModel: '1973 Mini Cooper',
      year: 2024,
      avatar: 'JW'
    },
    {
      id: 5,
      name: 'Lisa Van Der Merwe',
      location: 'Bloemfontein, SA',
      rating: 5,
      text: 'I inherited my grandfather\'s 1967 Mini and it needed extensive autobody work. The team treated it with the respect and care it deserved. The restoration is perfect and the sentimental value is priceless.',
      miniModel: '1967 Mini Cooper',
      year: 2024,
      avatar: 'LV'
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={`${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <section className="section-padding bg-gray-50 relative overflow-hidden">
      {/* Sparkle Effects */}
      <div className="absolute top-10 left-10 w-2 h-2 bg-mini-red rounded-full sparkle"></div>
      <div className="absolute top-20 right-20 w-3 h-3 bg-mini-green rounded-full sparkle" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-20 left-20 w-2 h-2 bg-mini-red rounded-full sparkle" style={{animationDelay: '2s'}}></div>
      <div className="absolute bottom-10 right-10 w-3 h-3 bg-mini-green rounded-full sparkle" style={{animationDelay: '0.5s'}}></div>
      
      <div className="container-custom relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center space-x-2 bg-mini-red/10 text-mini-red px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageCircle size={16} />
            <span>Testimonials</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 dark:text-white mb-6">
            What Our Customers Say
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what Mini enthusiasts have to say about our work.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          {/* Main Testimonial Display */}
          <div className="relative min-h-[400px] flex items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="w-full"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                  <div className="flex items-start space-x-6">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-mini-red to-mini-green rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {testimonials[currentIndex].avatar}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {/* Quote Icon */}
                      <Quote size={32} className="text-mini-red/20 mb-4" />
                      
                      {/* Stars */}
                      <div className="flex items-center space-x-1 mb-4">
                        {renderStars(testimonials[currentIndex].rating)}
                      </div>

                      {/* Testimonial Text */}
                      <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed italic">
                        "{testimonials[currentIndex].text}"
                      </p>

                      {/* Customer Info */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                              {testimonials[currentIndex].name}
                            </h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              {testimonials[currentIndex].location}
                            </p>
                          </div>
                          <div className="mt-2 sm:mt-0 text-right">
                            <p className="font-medium text-mini-red">
                              {testimonials[currentIndex].miniModel}
                            </p>
                            <p className="text-gray-500 text-sm">
                              Restored {testimonials[currentIndex].year}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-mini-red transition-colors z-10"
            >
              <ChevronLeft size={24} />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-mini-red transition-colors z-10"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-mini-red scale-125'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-mini-red/50'
                }`}
              />
            ))}
          </div>

          {/* Auto-play indicator */}
          <div className="text-center mt-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-mini-red transition-colors"
            >
              {isAutoPlaying ? 'Pause' : 'Resume'} auto-play
            </button>
          </div>
        </div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-gradient-to-r from-mini-red to-mini-green rounded-2xl p-8 text-white">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
              Ready to Join Our Happy Customers?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Let us bring your classic Mini back to its former glory.
            </p>
            <motion.a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center bg-white text-mini-red hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Your Quote
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
